/**
 * @file OperationsSitesPage.jsx — OPERATIONS department — Sites.
 * @description Half map, half list. The LEFT 50% is the map with every active
 *              contract site marked by its geofence (polygon + pin); the RIGHT
 *              50% lists all sites (customer, machines, fence state). Clicking
 *              a site opens the contract's LIVE view — the same design the
 *              rental/contract pages already use (/contract-live/:id). The
 *              crosshair button on a row just centres the map on that site.
 *
 * This file belongs to the OPERATIONS department. Departments never share page
 * files: a change for OPERATIONS is made HERE and cannot leak into another
 * department's screens. Backend: GET /api/geofencing/sites.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { setContractOrigin } from '../../../utils/contractOrigin';
import {
    Box, Typography, Card, Stack, Chip, Skeleton, Alert, IconButton, Tooltip, TextField, InputAdornment,
} from '@mui/material';
import {
    Place as SiteIcon, GpsFixed as LocateIcon, Search as SearchIcon,
    Engineering as MachinesIcon, ChevronRight as OpenIcon,
} from '@mui/icons-material';
import { MapContainer, TileLayer, Polygon, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import geofencingService from '../../../services/geofencingService';

// Default Leaflet marker (CRA asset paths).
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
const DefaultIcon = L.icon({ iconUrl: icon, shadowUrl: iconShadow, iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34] });
L.Marker.prototype.options.icon = DefaultIcon;

const FLEET_CENTER = [17.5, 78.5];

/**
 * [lat, lng] only for a real position: both values present, in range, and not
 * 0,0. A missing value must not pass as a number — Number(null) is 0 — or a
 * site nobody has located is drawn off the coast of Africa (0°, 0°), which is
 * also where a GPS with no fix reports itself.
 */
export const realLatLng = (lat, lng) => {
    if (lat == null || lng == null || lat === '' || lng === '') return null;
    const a = Number(lat), o = Number(lng);
    if (!Number.isFinite(a) || !Number.isFinite(o) || Math.abs(a) > 90 || Math.abs(o) > 180) return null;
    if (Math.abs(a) < 0.01 && Math.abs(o) < 0.01) return null;
    return [a, o];
};

const centroidOf = (polygon) => {
    if (!Array.isArray(polygon) || polygon.length === 0) return null;
    const pts = polygon.map(p => p && realLatLng(p.lat, p.lng)).filter(Boolean);
    if (!pts.length) return null;
    return [
        pts.reduce((a, p) => a + p[0], 0) / pts.length,
        pts.reduce((a, p) => a + p[1], 0) / pts.length,
    ];
};

/** Fly the map when a site is picked from the list; fit all fences at start. */
function MapController({ focus, bounds }) {
    const map = useMap();
    useEffect(() => {
        if (focus) map.flyTo(focus, 15, { duration: 0.8 });
    }, [focus, map]);
    useEffect(() => {
        if (!focus && bounds && bounds.length > 0) {
            try { map.fitBounds(bounds, { padding: [30, 30], maxZoom: 12 }); } catch { /* single point */ }
        }
        // Initial fit only — deliberately not re-fitting on every data poll.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, bounds ? bounds.length : 0]);
    return null;
}

const OperationsSitesPage = () => {
    const navigate = useNavigate();
    const [sites, setSites] = useState(null);
    const [error, setError] = useState('');
    const [focus, setFocus] = useState(null);        // [lat,lng] the map flies to
    const [search, setSearch] = useState('');

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await geofencingService.getSites();
                if (alive) setSites(res?.sites || []);
            } catch (e) {
                if (alive) { setError(e?.message || 'Failed to load sites'); setSites([]); }
            }
        })();
        return () => { alive = false; };
    }, []);

    // Every site that CAN be placed gets a pin: a drawn geofence gives its
    // centroid, and a site with no fence falls back to the centre of the
    // machines working there (server-computed from their last known GPS).
    const enriched = useMemo(() => (sites || []).map(s => {
        const fenceCenter = centroidOf(s.polygon);
        const machineCenter = realLatLng(s.lat, s.lng);
        const center = fenceCenter || machineCenter;
        return {
            ...s,
            center,
            fenced: Array.isArray(s.polygon) && s.polygon.length >= 3,
            fromMachines: !fenceCenter && !!machineCenter,
        };
    }), [sites]);

    // Sites we still cannot place: no fence drawn AND no machine has reported
    // a position (never-tracked or silent > 30 days). Surfaced as a caption so
    // an incomplete map is explained rather than silently short.
    const unplaced = useMemo(() => enriched.filter(s => !s.center), [enriched]);

    const shown = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return enriched;
        return enriched.filter(s =>
            String(s.site_location || '').toLowerCase().includes(q) ||
            String(s.company_name || '').toLowerCase().includes(q) ||
            String(s.rental_contract_id || '').toLowerCase().includes(q));
    }, [enriched, search]);

    const allBounds = useMemo(() =>
        enriched.filter(s => s.center).map(s => s.center), [enriched]);

    const openSite = (s) => { setContractOrigin('sites'); navigate(`/contract-live/${encodeURIComponent(s.rental_contract_id)}`); };

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }} className="animate-fade-in">
            {unplaced.length > 0 && (
                <Chip size="small" color="warning" variant="outlined" sx={{ mb: 2, fontWeight: 600 }}
                    label={`${unplaced.length} site${unplaced.length === 1 ? '' : 's'} not on the map`} />
            )}

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }, height: { md: 'calc(100vh - 210px)' }, minHeight: 480 }}>
                {/* LEFT 50% — the map with geofenced site locations */}
                <Card sx={{ flex: 1, minWidth: 0, borderRadius: '16px', overflow: 'hidden', minHeight: { xs: 340, md: 'auto' } }}>
                    <MapContainer center={FLEET_CENTER} zoom={5} style={{ height: '100%', width: '100%' }}>
                        <TileLayer
                            attribution='&copy; OpenStreetMap contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <MapController focus={focus} bounds={allBounds} />
                        {enriched.filter(s => s.fenced).map(s => (
                            <Polygon
                                key={`poly-${s.rental_contract_id}`}
                                positions={s.polygon.map(p => p && realLatLng(p.lat, p.lng)).filter(Boolean)}
                                pathOptions={{ color: '#2563eb', weight: 2, fillOpacity: 0.08 }}
                            />
                        ))}
                        {enriched.filter(s => s.center).map(s => (
                            <Marker key={`pin-${s.rental_contract_id}`} position={s.center}>
                                <Popup>
                                    <strong>{s.site_location || s.rental_contract_id}</strong><br />
                                    {s.company_name || ''}<br />
                                    {s.machine_count} machine{s.machine_count === 1 ? '' : 's'}<br />
                                    <span style={{ color: '#64748b' }}>
                                        {s.fenced ? 'Located by its geofence'
                                            : s.fromMachines ? `Located from ${s.located_machines} machine${s.located_machines === 1 ? '' : 's'} on site`
                                            : ''}
                                    </span><br />
                                    <span style={{ color: '#2563eb', cursor: 'pointer', fontWeight: 600 }}
                                        onClick={() => openSite(s)}>
                                        Open live view →
                                    </span>
                                </Popup>
                            </Marker>
                        ))}
                    </MapContainer>
                </Card>

                {/* RIGHT 50% — every site; click → the contract's live view */}
                <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.25, overflow: 'hidden' }}>
                    <TextField
                        size="small" placeholder="Search site, customer or contract…"
                        value={search} onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.disabled' }} /></InputAdornment>,
                            sx: { borderRadius: '12px', bgcolor: 'background.paper' },
                        }}
                    />
                    <Box sx={{ overflowY: 'auto', flex: 1, pr: 0.5 }}>
                        {sites === null ? (
                            <Stack spacing={1.25}>{[1, 2, 3, 4].map(i => <Skeleton key={i} variant="rounded" height={74} />)}</Stack>
                        ) : shown.length === 0 ? (
                            <Card variant="outlined" sx={{ borderRadius: '14px', p: 2.5 }}>
                                <Typography color="text.secondary" variant="body2">
                                    {enriched.length === 0
                                        ? 'No active contract sites right now.'
                                        : 'No site matches the search.'}
                                </Typography>
                            </Card>
                        ) : (
                            <Stack spacing={1.25}>
                                {shown.map(s => (
                                    <Card
                                        key={s.rental_contract_id}
                                        variant="outlined"
                                        onClick={() => openSite(s)}
                                        sx={{
                                            borderRadius: '14px', p: 1.75, cursor: 'pointer',
                                            transition: 'border-color .15s ease, box-shadow .15s ease',
                                            '&:hover': { borderColor: 'primary.main', boxShadow: '0 4px 14px rgba(37,99,235,.12)' },
                                        }}
                                    >
                                        <Stack direction="row" alignItems="center" spacing={1.5}>
                                            <SiteIcon sx={{ color: 'primary.main' }} />
                                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                                <Typography variant="body2" fontWeight={800} noWrap>
                                                    {s.site_location || s.rental_contract_id}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                                    {s.company_name || '—'} · {s.rental_contract_id}
                                                </Typography>
                                            </Box>
                                            <Chip size="small" icon={<MachinesIcon sx={{ fontSize: 14 }} />}
                                                label={`${s.machine_count}`} sx={{ fontWeight: 700 }} />
                                            <Chip size="small" variant="outlined"
                                                color={s.fenced ? 'success' : 'default'}
                                                label={s.fenced ? 'Geofenced' : 'No fence'} sx={{ fontWeight: 600 }} />
                                            {s.center && (
                                                <Tooltip title="Show on the map">
                                                    <IconButton size="small"
                                                        onClick={(e) => { e.stopPropagation(); setFocus([...s.center]); }}>
                                                        <LocateIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            <OpenIcon sx={{ color: 'text.disabled' }} />
                                        </Stack>
                                    </Card>
                                ))}
                            </Stack>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default OperationsSitesPage;
